import React, { useState } from 'react'
import { Modal, Form, Button } from 'react-bootstrap'
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateUserProfileMutation,
} from '../../../services/userApi'
import { useGetOrganizationPublicQuery } from '../../../services/orgsApi'
import { CreateUserReq, UpdateUserReq } from '../../../types/api/api-user'
import { User } from '../../../types/models/user'
import { toTitleCase } from '../../../utils'

interface CreateUserModalProps {
  show: boolean
  onHide: () => void
}

interface EditUserModalProps {
  show: boolean
  onHide: () => void
  user: User | null
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({ show, onHide }) => {
  const [createUser] = useCreateUserMutation()
  const { data: orgsData } = useGetOrganizationPublicQuery()
  const [showPassword, setShowPassword] = useState(false)
  const [newUserData, setNewUserData] = useState<CreateUserReq>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    password_confirm: '',
    org_id: '',
  })

  const handleCreateUser = async () => {
    try {
      await createUser(newUserData).unwrap()
      onHide()
      setNewUserData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        password_confirm: '',
        org_id: '',
      })
    } catch (err) {
      console.error('Failed to create user:', err)
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header className="bg-dark text-white">
        <Modal.Title>Create New User</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-white">
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              value={newUserData.username}
              onChange={(e) =>
                setNewUserData({
                  ...newUserData,
                  username: e.target.value.toLowerCase(),
                })
              }
              className="bg-dark text-white"
              autoComplete="off"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              className="bg-dark text-white"
              autoComplete="off"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              value={newUserData.first_name}
              onChange={(e) =>
                setNewUserData({
                  ...newUserData,
                  first_name: toTitleCase(e.target.value),
                })
              }
              className="bg-dark text-white"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              value={newUserData.last_name}
              onChange={(e) =>
                setNewUserData({
                  ...newUserData,
                  last_name: toTitleCase(e.target.value),
                })
              }
              className="bg-dark text-white"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Organization</Form.Label>
            <Form.Select
              value={newUserData.org_id || ''}
              onChange={(e) =>
                setNewUserData({
                  ...newUserData,
                  org_id: e.target.value || undefined,
                })
              }
              className="bg-dark text-white"
            >
              <option value="">Select an organization</option>
              <option key={orgsData?.id} value={orgsData?.id}>
                {orgsData?.name}
              </option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Password</Form.Label>
            <div className="input-group">
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                className="bg-dark text-white"
                autoComplete="new-password"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                className="d-flex align-items-center"
              >
                <i className={`fas fa-eye${showPassword ? '-slash' : ''}`}></i>
              </Button>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Confirm Password</Form.Label>
            <div className="input-group">
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={newUserData.password_confirm}
                onChange={(e) =>
                  setNewUserData({
                    ...newUserData,
                    password_confirm: e.target.value,
                  })
                }
                className="bg-dark text-white"
                autoComplete="new-password"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                className="d-flex align-items-center"
              >
                <i className={`fas fa-eye${showPassword ? '-slash' : ''}`}></i>
              </Button>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="bg-dark text-white">
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleCreateUser}>
          Create
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export const EditUserModal: React.FC<EditUserModalProps> = ({ show, onHide, user }) => {
  const [updateUser] = useUpdateUserMutation()
  const [updateUserProfile] = useUpdateUserProfileMutation()
  const { data: orgsData } = useGetOrganizationPublicQuery()
  const [editUserData, setEditUserData] = useState<UpdateUserReq>({
    email: '',
    first_name: '',
    last_name: '',
    org_id: '',
  })

  React.useEffect(() => {
    if (user) {
      setEditUserData({
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        org_id: user.org_id,
      })
    }
  }, [user])

  const handleUpdateUser = async () => {
    if (!user) return
    try {
      await updateUser({
        id: user.id,
        userData: editUserData,
      }).unwrap()
      onHide()
    } catch (err) {
      console.error('Failed to update user:', err)
    }
  }

  const handleProfileUpload = async (file: File) => {
    if (!user) return
    try {
      await updateUserProfile({ id: user.id, file }).unwrap()
    } catch (err) {
      console.error('Failed to upload profile:', err)
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header className="bg-dark text-white">
        <Modal.Title>Edit User</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-white">
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={editUserData.email}
              onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
              className="bg-dark text-white"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              value={editUserData.first_name}
              onChange={(e) => setEditUserData({ ...editUserData, first_name: e.target.value })}
              className="bg-dark text-white"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              value={editUserData.last_name}
              onChange={(e) => setEditUserData({ ...editUserData, last_name: e.target.value })}
              className="bg-dark text-white"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Organization</Form.Label>
            <Form.Select
              value={editUserData.org_id || ''}
              onChange={(e) =>
                setEditUserData({
                  ...editUserData,
                  org_id: e.target.value || undefined,
                })
              }
              className="bg-dark text-white"
            >
              <option value="">Select an organization</option>
              <option key={orgsData?.id} value={orgsData?.id}>
                {orgsData?.name}
              </option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Profile Picture</Form.Label>
            <Form.Control
              type="file"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0]
                if (file) {
                  handleProfileUpload(file)
                }
              }}
              className="bg-dark text-white"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="bg-dark text-white">
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleUpdateUser}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
